import * as _ from 'lodash';
import { getLogger } from '../../diagnostics/logger';
import moment from 'moment';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { MsisdnSwapCommand } from 'src/models/state/commands';
import {
  BaseSubmissionPayload,
  MsisdnSwapDetails,
  MsisdnSwapSubmissionPayload
} from 'src/models/state/submissionPayloads';
import resourceStrings from '../../ResourceStrings';
import {
  BULK_CONFIGURE_SPEND_CAP,
  BULK_DELETE_SPEND_CAP,
  CANCEL_SIM_SUBMISSION,
  CANCEL_SIM_SUBMISSION_FAILURE,
  CANCEL_SIM_SUBMISSION_SUCCESS,
  COMPLETE_SIM_SUBMISSION,
  COMPLETE_SIM_SUBMISSION_FAILURE,
  COMPLETE_SIM_SUBMISSION_SUCCESS,
  CREATE_SIM_SUBMISSION,
  CREATE_SIM_SUBMISSION_FAILURE,
  CREATE_SIM_SUBMISSION_SUCCESS,
  ENSURE_SELECTED_SIM_SUBMISSION,
  ENSURE_SIM_SUBMISSIONS,
  EXPORT_SIM_SUBMISSION,
  INITIAL_AUTHENTICATION,
  LOAD_SELECTED_SIM_SUBMISSION,
  LOAD_SIM_SUBMISSIONS
} from '../../actions/actionTypes';
import { Action, PromiseAction } from '../../actions/commonActions';
import { showImpersonationBlocked } from '../../actions/impersonationActions';
import { addNotification } from '../../actions/notificationActions';
import {
  cancelSimSubmissionFailure,
  cancelSimSubmissionSuccess,
  clearSelectedSimSubmission,
  completeSimSubmissionFailure,
  completeSimSubmissionSuccess,
  createSimSubmissionFailure,
  createSimSubmissionSuccess,
  loadSelectedSimSubmission,
  loadSelectedSimSubmissionFailure,
  loadSelectedSimSubmissionSuccess,
  loadSimSubmissionsFailure,
  loadSimSubmissionsSuccess
} from '../../actions/simSubmissionActions';
import * as submissionApi from '../../api/simSubmissionApi';
import * as simApi from '../../api/simApi';
import { SimSubmissionSearchOptions } from '../../api/utilities';
import { HashMap } from '../../constants';
import { Loadable } from '../../models';
import { SimManagementError } from '../../models/Errors';
import {
  CancelSubmissionPayload,
  CompleteSubmissionPayload,
  SimSubmissionPayload
} from '../../models/payload/simSubmission';
import { QueryResult } from '../../models/queryResult';
import { SimSubmissionPageableSearchFilter } from '../../models/searchFilter';
import { Sim } from '../../models/sim';
import { SimSubmissionsSuccessResponse } from '../../models/simSubmission';
import { AppState } from '../../models/state/appState';
import { SubmissionsState } from '../../models/state/submissionsState';
import { SimSubmission } from '../../models/transfer/input/SimSubmission';
import { selectSimSubmission } from '../../selectors/filter';
import { selectSelectedSims, selectViewedSims } from '../../selectors/sims';
import { selectSubmissions } from '../../selectors/submissions';
import { ProvisionSubmissionRequestStock } from '../../models/state/commands';
import { EditPoCommandOptions } from '../../models/payload/editPo';

const log = getLogger('simmanagement.sagas.login');

export const getSimSubmissionByTicketNumberSelector: (
  state: AppState,
  ticketNumber: string
) => SimSubmission | undefined = (state, ticketNumber) =>
  state.filter.simSubmission && state.filter.simSubmission.ticketNumber === ticketNumber
    ? state.filter.simSubmission
    : state.submissions.submissionsByTicketNumber[ticketNumber];

interface BaseCommand {
  serialNumbers: string[];
}

function convertDateToUtc(date: Date): Date {
  let utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));

  return utcDate;
}

function convertDataToCustomPayload(
  submission: SimSubmissionPayload,
  selectedSims: HashMap<Sim>
): BaseSubmissionPayload {
  switch (submission.type) {
    case 'msisdn-swap':
      var indexCommand = submission.simCommands[0] as MsisdnSwapCommand;
      var swaps = [] as MsisdnSwapDetails[];

      for (let i = 0; i < indexCommand.stockIds.length; i++) {
        const selectedSim = selectedSims[indexCommand.stockIds[i]];
        if (!selectedSim) {
          continue;
        }
        swaps.push({
          newMsisdn: indexCommand.swaps[i],
          serialNumber: selectedSim.serialNumber
        });
      }

      return {
        name: submission.name,
        type: 'msisdn-swap',
        actionDate:
          submission.actionDate == undefined ? submission.actionDate : convertDateToUtc(submission.actionDate),
        swaps: swaps
      } as MsisdnSwapSubmissionPayload;
    default:
      return {
        name: submission.name,
        type: submission.type,
        actionDate: submission.actionDate == undefined ? submission.actionDate : convertDateToUtc(submission.actionDate)
      } as BaseSubmissionPayload;
  }
}

/**
 * Converts data into format for sending to server
 * @param submission The submission
 * @param selectedSims A hash map of selected SIMs, indexed by stock identifier.
 */
function convertData(submission: SimSubmissionPayload, selectedSims: HashMap<Sim>) {
  const normalizeOrderValue = (poValue: string | null | undefined): string | null => {
    if (typeof poValue !== 'string') {
      return null;
    }

    return poValue.replace(/,/gi, '').trim();
  };

  const result = {
    type: submission.type,
    name: submission.name,
    actionDate: submission.actionDate == undefined ? submission.actionDate : convertDateToUtc(submission.actionDate),
    commands: submission.simCommands.map((command) => {
      const baseCommand: BaseCommand = {
        serialNumbers: command.stockIds
          .map((stockId) => {
            const sim = selectedSims[stockId];
            if (!sim) {
              log.warn(`SIM not selected so ignored: ${stockId}`);
            }

            return (sim && sim.serialNumber) || '';
          })
          // filter out any unknown SIMs (although there should not be any)
          .filter((s) => s.length > 0)
      };

      const type = command.definitionAlias;
      const { accountId, networkId } = command;
      switch (command.definitionAlias) {
        case 'configure-spend-cap':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              cap:
                command.cap !== null && command.cap !== undefined
                  ? parseFloat(command.cap.toString()).toFixed(2)
                  : undefined,
              capName: command.capName || undefined,
              accountId: command.accountId,
              networkId: command.networkId,
              serialNumbers: command.serialNumbers,
              name: command.name,
              barsToPersist: command.barsToPersist,
              smsOptOut: command.smsOptOut,
              notifyEmailAddresses: command.notifyEmailAddresses
            }
          };
        case 'remove-spend-cap':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              serialNumbers: command.serialNumbers
            }
          };
        case 'esimsmart-usage':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              startDate: command.startDate,
              endDate: command.endDate
            }
          };
        case 'basic-bar': {
        }
        case 'basic-unbar': {
        }
        case 'check-serial':
        case 'orange-remove-bundle':
        case 'orange-reset-bundle':
        case 'sim-status-report':
        case 'sim-refresh':
        case 'esimsmart-remove-bundle':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId
            }
          };
        case 'esimsmart-create-bundle': {
          const { bundleSize, period, setStartDate, startDate, willExpire, expirationDate, template } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              bundleSizeMb: bundleSize,
              bundlePeriod: period,
              expirationDate: willExpire ? expirationDate : undefined,
              startDate: setStartDate ? startDate : undefined,
              endDate: willExpire ? expirationDate : undefined,
              provisioningTemplateId: template
            }
          };
        }
        case 'esimsmart-change-bundle': {
          const { bundleSize, period, setStartDate, startDate, willExpire, expirationDate } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              bundleSizeMb: bundleSize,
              bundlePeriod: period,
              expirationDate: willExpire ? expirationDate : undefined,
              startDate: setStartDate ? startDate : undefined,
              endDate: willExpire ? expirationDate : undefined
            }
          };
        }
        case 'lift-spend-cap':
          return {
            ...baseCommand,
            commandOptions: {
              type
            }
          };
        case 'top-up': {
          const { bundleSize } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              AmountMb: bundleSize
            }
          };
        }
        case 'migrate-out':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              accountId,
              backDate: command.backDate,
              networkId,
              keepSim: command.keepSim === null ? false : command.keepSim
            }
          };
        case 'unprovision':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              serialNumbers: baseCommand.serialNumbers
            }
          };
        case 'port-out':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              accountId,
              backDate: command.backDate,
              networkId,
              targetNetwork: command.targetNetwork
            }
          };
        case 'esimsmart-detailed-bar':
        case 'esimsmart-detailed-unbar': {
          const {
            admin,
            outgoingCalls,
            outgoingInternationalCalls,
            incomingCalls,
            roamingOutgoingInternationalCalls,
            roaming,
            roamingIncomingCalls,
            roamingOutgoingCalls,
            gprs
          } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              admin,
              outgoingCalls,
              outgoingInternationalCalls,
              incomingCalls,
              roamingOutgoingInternationalCalls,
              roaming,
              roamingIncomingCalls,
              roamingOutgoingCalls,
              gprs
            }
          };
        }
        case 'o2-detailed-bar':
        case 'o2-detailed-unbar': {
          const {
            gprs,
            smsIncoming,
            internationalCalls,
            outgoingCalls,
            outgoingInternationalCalls,
            smsOutgoing,
            mms,
            premiumCalls,
            smsPremium,
            roaming,
            voice,
            wifiCalling,
            enable5G,
            chargeableCalls
          } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              gprs,
              internationalCalls,
              outgoingCalls,
              outgoingInternationalCalls,
              mms,
              premiumCalls,
              roaming,
              smsIncoming,
              smsOutgoing,
              smsPremium,
              voice,
              wifiCalling,
              enable5G,
              chargeableCalls
            }
          };
        }
        case 'change-provision':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              accountId,
              networkId,
              actionDate:
                submission.actionDate == undefined ? submission.actionDate : convertDateToUtc(submission.actionDate),
              backDate: command.backDate,
              boltOns: command.boltOnsString === '' ? [] : command.boltOnsString.split(','),
              createNewProvisioningOrder: command.createNewProvisioningOrder,
              newProvisioningOrderName:
                (command.createNewProvisioningOrder && command.newProvisioningOrderName) || null,
              pac: command.pac,
              provisioningOrderNumber: (!command.createNewProvisioningOrder && command.provisioningOrderNumber) || null,
              tariffNumber: command.tariffNumber,
              publicIpAdress: command.publicIpAddress,
              staticIpAddress: command.staticIpAddress,
              publicIpGroupId: command.publicIpGroup,
              staticIpGroupId: command.staticIpGroup,
              stock: baseCommand.serialNumbers.map((x) => {
                var stockItem: ProvisionSubmissionRequestStock = {
                  serialNumber: x,
                  msisdn: GetTelNumberFromSelectedSim(selectedSims, x)
                };
                return stockItem;
              })
            }
          };
        case 'edit-po':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              accountId,
              networkId,
              // preserving null values to ensure existing values are not changed on the server
              order1: normalizeOrderValue(command.poNumber1),
              order2: normalizeOrderValue(command.poNumber2),
              order3: normalizeOrderValue(command.poNumber3),
              order4: normalizeOrderValue(command.poNumber4),
              order5: normalizeOrderValue(command.poNumber5)
            } as EditPoCommandOptions
          };
        case 'migrate-in':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              backDate: command.backDate,
              accountId,
              networkId,
              boltOns: command.boltOnsString === '' ? [] : command.boltOnsString.split(','),
              createNewProvisioningOrder: command.createNewProvisioningOrder,
              newProvisioningOrderName:
                (command.createNewProvisioningOrder && command.newProvisioningOrderName) || null,
              pac: command.pac,
              provisioningOrderNumber: (!command.createNewProvisioningOrder && command.provisioningOrderNumber) || null,
              purchaseOrderNumber: command.purchaseOrderNumber,
              tariffNumber: command.tariffNumber,
              tels: command.tels,
              keepSim: command.keepSim === null ? false : command.keepSim,
              stock: baseCommand.serialNumbers.map((serialNumber, index) => ({
                serialNumber,
                msisdn: command.tels[index] ?? GetTelNumberFromSelectedSim(selectedSims, serialNumber)
              })) as ProvisionSubmissionRequestStock[]
            }
          };
        case 'port-in':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              backDate: command.backDate,
              accountId,
              networkId,
              boltOns: command.boltOnsString === '' ? [] : command.boltOnsString.split(','),
              createNewProvisioningOrder: command.createNewProvisioningOrder,
              newProvisioningOrderName:
                (command.createNewProvisioningOrder && command.newProvisioningOrderName) || null,
              pac: command.pac,
              provisioningOrderNumber: (!command.createNewProvisioningOrder && command.provisioningOrderNumber) || null,
              purchaseOrderNumber: command.purchaseOrderNumber,
              sourceNetwork: command.sourceNetwork,
              tariffNumber: command.tariffNumber,
              tels: command.tels
            }
          };
        case 'provision':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              actionDate:
                submission.actionDate == undefined ? submission.actionDate : convertDateToUtc(submission.actionDate),
              backDate: command.backDate,
              accountId,
              networkId,
              boltOns: command.boltOnsString === '' ? [] : command.boltOnsString.split(','),
              createNewProvisioningOrder: command.createNewProvisioningOrder,
              newProvisioningOrderName:
                (command.createNewProvisioningOrder && command.newProvisioningOrderName) || null,
              pac: command.pac,
              provisioningOrderNumber: (!command.createNewProvisioningOrder && command.provisioningOrderNumber) || null,
              provisionType: command.provisionType,
              purchaseOrderNumber: command.purchaseOrderNumber,
              tariffNumber: command.tariffNumber,
              tels: command.tels,
              shutdownThreshold: command.shutdownThreshold,
              bundleSizeMb: command.bundlesizeMb,
              publicIpGroupId: command.publicIpGroup,
              staticIpGroupId: command.staticIpGroup,
              stock: baseCommand.serialNumbers.map((x, index) => {
                if (command.tels.length == 0 || command.tels[index] == null) {
                  var stockItem: ProvisionSubmissionRequestStock = {
                    serialNumber: x,
                    msisdn: GetTelNumberFromSelectedSim(selectedSims, x)
                  };
                  return stockItem;
                } else {
                  var msisdn = command.tels[index];
                  var stockItem: ProvisionSubmissionRequestStock = { serialNumber: x, msisdn: msisdn };
                  return stockItem;
                }
              })
            }
          };
        case 'configure-sms-alerts': {
          const {
            emailBcc,
            emailCc,
            emailTo,
            additionalTel,
            alertType,
            alertLevel,
            alertMessage,
            warningLevel,
            warningMessage
          } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              additionalTel,
              alertLevel,
              alertMessage,
              alertType,
              emailBcc,
              emailCc,
              emailTo,
              warningLevel,
              warningMessage
            }
          };
        }
        case 'remove-sms-alerts':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              alertType: command.alertType
            }
          };
        case 'orange-create-bundle': {
          const {
            bundleSize,
            bundlePeriodUnit,
            bundlePeriodValue,
            endOfBundleAction,
            setStartDate,
            startDate,
            endDate,
            willExpire,
            zone
          } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              bundleSize,
              bundlePeriodUnit,
              bundlePeriodValue,
              endOfBundleAction,
              startDate: setStartDate ? startDate : undefined,
              endDate: willExpire ? endDate : undefined,
              zone
            }
          };
        }
        case 'orange-modify-bundle': {
          const { bundlePeriodUnit, bundlePeriodValue, bundleSize, endDate, endOfBundleAction, usingVoice, zone } =
            command;
          return {
            ...baseCommand,
            commandOptions: {
              bundlePeriodUnit,
              bundlePeriodValue,
              bundleSize,
              endDate,
              endOfBundleAction,
              networkId,
              type,
              usingVoice,
              zone
            }
          };
        }
        case 'orange-top-up':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              bundleSize: command.bundleSize,
              zone: command.zone
            }
          };
        case 'orange-usage':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              startDate: command.startDate,
              endDate: command.endDate
            }
          };
        case 'voda-detailed-bar':
        case 'voda-detailed-unbar': {
          const {
            admin,
            arkessaApn,
            conferenceCalling,
            gprs,
            gprsRoaming,
            international,
            outgoing,
            mms,
            premium,
            smsIncoming,
            smsOutgoing,
            smsPremium,
            voiceRoaming,
            wifiCalling,
            enable5G,
            intxHome
          } = command;
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              admin,
              arkessaApn,
              conferenceCalling,
              gprs,
              gprsRoaming,
              international,
              outgoing,
              mms,
              premium,
              smsIncoming,
              smsOutgoing,
              smsPremium,
              voiceRoaming,
              wifiCalling,
              enable5G,
              intxHome
            }
          };
        }
        case 'reconfigure-sim-profile':
          return {
            ...baseCommand,
            commandOptions: {
              accountId,
              type,
              networkId,
              order: command.order,
              services: command.services,
              smsAlerts: command.smsAlerts,
              spendCap: command.spendCap
            }
          };
        case 'bar-service-config':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              services: command.services
            }
          };
        case 'sim-swap':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              simSwaps: command.simSwaps
            }
          };
        case 'msisdn-swap':
          return {
            ...baseCommand,
            commandOptions: {
              type,
              networkId,
              swaps: command.swaps
            }
          };
        case 'change-account': {
          return {
            ...baseCommand,
            commandOptions: {
              type,
              moveToAccountId: command.moveToAccountId
            }
          };
        }
        case 'export-esim': {
          return {
            ...baseCommand,
            commandOptions: {
              type,
              emailAddress: command.emailAddress
            }
          };
        }
        case 'configure-bundle-control': {
          return {
            ...baseCommand,
            commandOptions: {
              type,
              name: command.name,
              serialNumbers: command.serialNumbers,
              zone: command.zone,
              bundleControlPeriod: command.bundleControlPeriod,
              sizeMb: command.sizeMb,
              startDate: command.startDate,
              endDate: command.endDate,
              bundleControlEndAction: command.bundleControlEndAction,
              roamingBucketVolumes: command.roamingBucketVolumes
            }
          };
        }
        case 'configure-fair-usage': {
          return {
            ...baseCommand,
            commandOptions: {
              type,
              name: command.name,
              serialNumbers: command.serialNumbers,
              buckets: command.buckets
            }
          };
        }
        case 'remove-bundle-control': {
          return {
            ...baseCommand,
            commandOptions: {
              type,
              name: command.name,
              serialNumbers: command.serialNumbers
            }
          };
        }
        case 'send-sms': {
          return {
            ...baseCommand,
            commandOptions: {
              type
            }
          };
        }

        case 'configure-threshold': {
          return {
            ...baseCommand,
            commandOptions: {
              type,
              shutdownThreshold: command.shutdownThreshold
            }
          };
        }
        default:
          throw new Error('Unknown command type');
      }
    })
  };

  return result;
}

export const GetTelNumberFromSelectedSim = (selectedSims: HashMap<Sim>, serialNo: string): string | undefined => {
  for (const property in selectedSims) {
    if (selectedSims.hasOwnProperty(property)) {
      const sim = selectedSims[property];
      if (sim && sim.serialNumber == serialNo) {
        return sim.tel == '' ? undefined : sim.tel;
      }
    }
  }

  return undefined;
};

/*
 * A saga which creates a submission on the server, requesting that certain SIM actions are taken.
 * @param action
 */
export function* createSimSubmissionSaga(action: PromiseAction<SimSubmissionPayload, string>) {
  const payload = action.payload;
  if (!payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    action.reject && action.reject('No payload.');
    return;
  }

  try {
    const simsById: HashMap<Sim> = payload.viewedSim
      ? yield select(selectViewedSims)
      : yield select(selectSelectedSims);

    let submissionData;
    switch (payload.type) {
      case 'msisdn-swap':
        submissionData = convertDataToCustomPayload(payload, simsById);
        break;
      default:
        submissionData = convertData(payload, simsById);
        break;
    }

    const submissionId: string = yield call(submissionApi.create, submissionData);
    yield put(createSimSubmissionSuccess(submissionId));
    action.resolve && action.resolve(submissionId);
  } catch (error: any) {
    const err = error as SimManagementError;
    yield put(createSimSubmissionFailure(err));
    action.reject && action.reject(err);
  }
}

export function* createSimSubmissionFailureSaga(action: Action<SimManagementError>) {
  const payload = action.payload;
  if (!payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  // A restricted impersonator gets a 403 from every submission write. Non-impersonating users can
  // also receive a 403 from the API's command restrictions, so only claim impersonation when we are.
  const impersonatorId: string | undefined = yield select(
    (state: AppState) => state.loginDetails.user?.impersonatorId
  );
  if (payload.statusCode === 403 && impersonatorId) {
    yield put(showImpersonationBlocked());
    return;
  }

  const anyValidationErrors = _.size(payload.validationErrors) > 0;

  let detail: string;

  try {
    if (anyValidationErrors) {
      detail =
        typeof payload.validationErrors === 'string'
          ? payload.validationErrors
          : Object.keys(payload.validationErrors as Record<string, string[]>)
              .map((field) => `${field}: ${payload.validationErrors?.[field]?.join(', ')}.`)
              .join(' ');
    } else {
      if ((payload.statusCode === 400 || payload.statusCode === 409) && payload.message && payload.message !== "") {
        detail = payload.message;
      } else {
        detail = resourceStrings.errorOnSimSubmission;
      }
    }
  } catch (error: any) {
    detail = resourceStrings.errorOnSimSubmission;
  }

  yield put(
    addNotification({
      category: 'task',
      title: 'Error',
      detail: detail,
      type: 'error',
      showInCount: false
    })
  );
}

export function* createSimSubmissionSuccessSaga(action: Action<string>) {
  const payload = action.payload;
  if (!payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  yield put(
    addNotification({
      category: 'task',
      title: 'Submitted',
      detail: `Your SIM action request has been submitted successfully.`,
      type: 'success',
      showInCount: false
    })
  );
}

export function* exportSimSubmissionSaga(action: Action<string>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  const ticketNumber = action.payload;

  try {
    const blob: Blob = yield call(submissionApi.exportToFile, ticketNumber);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `export_${ticketNumber}.xlsx`);
    document.body.appendChild(link);
    link.click();
    /* yield put(exportSimSubmissionSuccess(ticketNumber, blob)); */
  } catch (err: any) {
    const error = err as SimManagementError;
    if (error.message) {
      log.error(error.message);
    }

    const errorMessage = error.getStandardHttpError() || 'an error occurred';
    yield put(
      addNotification({
        category: 'task',
        title: 'Error',
        detail: `Export failed - ${errorMessage}.`,
        type: 'error',
        showInCount: false
      })
    );
  }
}

/**
 * Ensure that the selected company is present in the store and will initiate a server load if not.
 * @param action The action that invoked the saga.
 */
export function* ensureSelectedSimSubmissionSaga(action: Action<string>) {
  if (action.payload) {
    const submission: Loadable<SimSubmission> = yield select(selectSimSubmission);
    const ticketNumber = action.payload;
    if (
      submission.ticketNumber.toLowerCase() !== ticketNumber.toLowerCase() ||
      (submission.expiresOn && submission.expiresOn < new Date())
    ) {
      yield put(loadSelectedSimSubmission(ticketNumber));
    }
  }
}

export function* loadSelectedSimSubmissionSaga(action: Action<string>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  const ticketNumber = action.payload;
  try {
    const submission: SimSubmission = yield call(submissionApi.getByTicketNumber, ticketNumber, true);
    for (const command of submission.commands) {
      if (
        command.commandType.type === 'esimsmart-create-bundle' &&
        command.commandType.provisioningTemplateId &&
        (!command.commandType.accountId || !command.commandType.networkId)
      ) {
        const serialNumber = command.stocks && command.stocks[0] && command.stocks[0].serialNumber;
        if (serialNumber) {
          try {
            const sim: Sim = yield call(simApi.getBySerial, serialNumber);
            command.commandType.accountId = command.commandType.accountId || sim.accountId;
            command.commandType.networkId = command.commandType.networkId || sim.networkId;
          } catch (error: any) {
            log.warn(`Could not load SIM ${serialNumber} to resolve create bundle template details.`);
          }
        }
      }
    }
    yield put(loadSelectedSimSubmissionSuccess(submission));
  } catch (error: any) {
    yield put(loadSelectedSimSubmissionFailure(ticketNumber, error));
  }
}

/*
 * A saga which fetches the list of submissions.
 */
export function* loadSimSubmissionsSaga(
  action: PromiseAction<SimSubmissionPageableSearchFilter, SimSubmissionsSuccessResponse>
) {
  const searchFilter = action.payload;
  if (!searchFilter) {
    return;
  }

  try {
    const searchOptions: Partial<SimSubmissionSearchOptions> = {
      filters: searchFilter.filters,
      isScrollDown: searchFilter.isScrollDown,
      sortModel: searchFilter.sortModel,
      companyId: searchFilter.companyId,
      accountId: searchFilter.accountId,
      searchTerm: searchFilter.searchTerm,
      searchType: searchFilter.searchType,
      skip: searchFilter.startIndex,
      wrapWith: {
        totalCount: searchFilter.startIndex === 0
      }
    };

    if (searchFilter.stopIndex) {
      searchOptions.take = searchFilter.stopIndex - searchFilter.startIndex + 1;
    }

    const submissionsFromApi: QueryResult<SimSubmission> = yield call(submissionApi.get, searchOptions);
    yield put(
      loadSimSubmissionsSuccess({
        searchFilter,
        submissions: submissionsFromApi
      })
    );

    // invalidate filter.submission if necessary
    const filterSubmission: Loadable<SimSubmission> = yield select(selectSimSubmission);
    const match = submissionsFromApi.result.find((s) => s.ticketNumber === filterSubmission.ticketNumber);
    if (
      match &&
      // status has changed or there has been a cancel request
      (match.status !== filterSubmission.status || (match.cancelledOn && !filterSubmission.cancelledOn))
    ) {
      yield put(clearSelectedSimSubmission());
    }

    const response: SimSubmissionsSuccessResponse = {
      ...searchOptions,
      totalCount: submissionsFromApi.totalCount,
      searchFilter,
      submissions: submissionsFromApi
    };

    action.resolve && action.resolve(response);
  } catch (error: any) {
    yield put(loadSimSubmissionsFailure(searchFilter, error));
    console.error(error);
  }
}

/*
 * A saga which fetches the list of SIM submissions if needed
 */
export function* ensureSimSubmissionsSaga(action: Action<SimSubmissionPageableSearchFilter>) {
  const payload = action.payload;
  if (!payload) {
    return;
  }

  const simsState: SubmissionsState = yield select(selectSubmissions);
  const { companyId, accountId } = simsState.searchFilter;
  if (
    companyId !== payload.companyId ||
    accountId !== payload.accountId ||
    (simsState.expiresOn && simsState.expiresOn < moment().toDate()) ||
    simsState.searchFilter.filters !== payload.filters ||
    simsState.searchFilter.sortModel !== payload.sortModel
  ) {
    yield call(loadSimSubmissionsSaga, action);
  }
}

/**
 * A saga which contacts the server to request the cancellation of a submission.
 * @param action
 */
export function* cancelSimSubmissionSaga(action: PromiseAction<CancelSubmissionPayload, SimSubmission>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    action.reject && action.reject('No payload.');
    return;
  }

  try {
    const { ticketNumber, reason } = action.payload;
    const submission: SimSubmission = yield select(getSimSubmissionByTicketNumberSelector, ticketNumber);
    if (!submission) {
      log.warn(`Attempted to request cancellation of submission ${ticketNumber}, but submission was not found.`);
      return;
    }

    yield call(submissionApi.cancel, ticketNumber, reason);
    const includeCommands = submission.commands.length > 0;
    const updatedSubmission: SimSubmission = yield call(submissionApi.getByTicketNumber, ticketNumber, includeCommands);
    yield put(cancelSimSubmissionSuccess(updatedSubmission));
    action.resolve && action.resolve(updatedSubmission);
  } catch (error: any) {
    const err = error as SimManagementError;
    yield put(cancelSimSubmissionFailure(action.payload, err));
    action.reject && action.reject(err);
  }
}

export function* cancelSimSubmissionFailureSaga(action: Action<SimManagementError>) {
  const payload = action.payload;
  if (!payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  // A restricted impersonator gets a 403 from every submission write. Non-impersonating users can
  // also receive a 403 from the API's command restrictions, so only claim impersonation when we are.
  const impersonatorId: string | undefined = yield select(
    (state: AppState) => state.loginDetails.user?.impersonatorId
  );
  if (payload.statusCode === 403 && impersonatorId) {
    yield put(showImpersonationBlocked());
    return;
  }

  const { validationErrors } = payload;
  const errors = (validationErrors && validationErrors.ticketNumber && validationErrors.ticketNumber.join(' ')) || '';
  yield put(
    addNotification({
      category: 'task',
      title: 'Error',
      detail: `An error occurred on attempting to submit your cancellation request. ${errors}`,
      type: 'error',
      showInCount: false
    })
  );
}

export function* cancelSimSubmissionSuccessSaga(action: Action<SimSubmission>) {
  const payload = action.payload;
  if (!payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  yield put(
    addNotification({
      category: 'task',
      title: 'Cancellation Requested',
      detail: `Your cancellation request has been submitted successfully.`,
      type: 'success',
      showInCount: false
    })
  );
}

export function* completeSimSubmissionSaga(action: PromiseAction<CompleteSubmissionPayload, SimSubmission>) {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    action.reject && action.reject('No payload.');
    return;
  }

  try {
    const { ticketNumber, status, reason, msisdns, serialNumbers, lpaStrings } = action.payload;
    const submission: SimSubmission = yield select(getSimSubmissionByTicketNumberSelector, ticketNumber);
    if (!submission) {
      log.warn(`Attempted to manually complete submission ${ticketNumber}, but submission was not found.`);
      return;
    }

    yield call(submissionApi.complete, ticketNumber, status, reason, msisdns, serialNumbers, lpaStrings);
    const includeCommands = submission.commands.length > 0;
    const updatedSubmission: SimSubmission = yield call(submissionApi.getByTicketNumber, ticketNumber, includeCommands);
    yield put(completeSimSubmissionSuccess(updatedSubmission));
    action.resolve && action.resolve(updatedSubmission);
  } catch (error: any) {
    const err = error as SimManagementError;
    yield put(completeSimSubmissionFailure(action.payload, err));
    action.reject && action.reject(err);
  }
}

export function* completeSimSubmissionFailureSaga(action: Action<SimManagementError>) {
  const payload = action.payload;
  if (!payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  yield put(
    addNotification({
      category: 'task',
      title: 'Error',
      detail: 'An error occurred while updating the submission.',
      type: 'error',
      showInCount: false
    })
  );
}

export function* completeSimSubmissionSuccessSaga(action: Action<SimSubmission>) {
  const payload = action.payload;
  if (!payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  yield put(
    addNotification({
      category: 'task',
      title: 'Submission Updated',
      detail:
        payload.status === 'cancelled'
          ? 'The submission has been cancelled.'
          : 'The submission has been marked as complete.',
      type: 'success',
      showInCount: false
    })
  );
}

/*
 * Initializes all sagas that are related to SIM submissions.
 */
export function* initSimSubmissionSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(CANCEL_SIM_SUBMISSION, cancelSimSubmissionSaga);
  yield takeEvery(CANCEL_SIM_SUBMISSION_FAILURE, cancelSimSubmissionFailureSaga);
  yield takeEvery(CANCEL_SIM_SUBMISSION_SUCCESS, cancelSimSubmissionSuccessSaga);
  yield takeEvery(COMPLETE_SIM_SUBMISSION, completeSimSubmissionSaga);
  yield takeEvery(COMPLETE_SIM_SUBMISSION_FAILURE, completeSimSubmissionFailureSaga);
  yield takeEvery(COMPLETE_SIM_SUBMISSION_SUCCESS, completeSimSubmissionSuccessSaga);
  yield takeEvery(CREATE_SIM_SUBMISSION, createSimSubmissionSaga);
  yield takeEvery(CREATE_SIM_SUBMISSION_FAILURE, createSimSubmissionFailureSaga);
  yield takeEvery(CREATE_SIM_SUBMISSION_SUCCESS, createSimSubmissionSuccessSaga);
  yield takeEvery(EXPORT_SIM_SUBMISSION, exportSimSubmissionSaga);
  yield takeEvery(ENSURE_SELECTED_SIM_SUBMISSION, ensureSelectedSimSubmissionSaga);
  yield takeEvery(ENSURE_SIM_SUBMISSIONS, ensureSimSubmissionsSaga);
  yield takeEvery(LOAD_SELECTED_SIM_SUBMISSION, loadSelectedSimSubmissionSaga);
  yield takeEvery(LOAD_SIM_SUBMISSIONS, loadSimSubmissionsSaga);
  yield takeEvery(BULK_CONFIGURE_SPEND_CAP, createSimSubmissionSaga);
  yield takeEvery(BULK_DELETE_SPEND_CAP, createSimSubmissionSaga);
}
